'use client';

import { SearchContactsTimeRangeType } from '@aws-sdk/client-connect';
import {
  Button,
  Calendar,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@@/ui/primitives';
import { CalendarDaysIcon } from 'lucide-react';
import React, { useState } from 'react';
import { type DateRange } from 'react-day-picker';
import { getDefaultTimeline } from '.';

const items = [
  { value: 'INITIATION_TIMESTAMP', label: 'Initiation Timestamp' },
  { value: 'SCHEDULED_TIMESTAMP', label: 'Scheduled Timestamp' },
  { value: 'CONNECTED_TO_AGENT_TIMESTAMP', label: 'Connected to Agent Timestamp' },
  { value: 'DISCONNECT_TIMESTAMP', label: 'Disconnect Timestamp' },
  { value: 'ENQUEUE_TIMESTAMP', label: 'Enqueue Timestamp' }
];

export function FilterTimeline(props: {
  disabled: boolean;
  selected: { startTime: string; endTime: string; type: SearchContactsTimeRangeType };
  onSelect: (value: { startTime: string; endTime: string; type: SearchContactsTimeRangeType }) => void;
}) {
  const isDevelopment = process.env.NODE_ENV === 'development';

  const defaultTime = getDefaultTimeline();

  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: new Date(defaultTime.startTime),
    to: new Date(defaultTime.endTime)
  });

  // only allow 56 days of range
  const maxRange = 56 * 24 * 60 * 60 * 1000; // 56 days in milliseconds
  const handleDateRangeChange = (range: DateRange | undefined) => {
    if (range && range.from && range.to) {
      const selectedRange = range.to.getTime() - range.from.getTime();
      if (selectedRange > maxRange) {
        // If the selected range is too large, adjust the end date
        range.to = new Date(range.from.getTime() + maxRange);
      }
    }
    setDateRange(range);
  };

  return (
    <div className='flex flex-col gap-2'>
      <Select
        disabled={!isDevelopment}
        value={props.selected.type}
        onValueChange={(value) => {
          props.onSelect({
            ...props.selected,
            type: value as SearchContactsTimeRangeType
          });
        }}
      >
        <SelectTrigger className='text-sm!'>
          <SelectValue placeholder='Select a type' />
        </SelectTrigger>
        <SelectContent className='p-0 font-sans font-medium text-sm!' align='start'>
          <SelectGroup>
            {items.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
      <Dialog>
        <DialogTrigger asChild>
          <Button variant='outline' className='text-sm! text-center! gap-2' disabled={props.disabled}>
            <CalendarDaysIcon className='size-4' />
            {new Date(props.selected.startTime).toLocaleDateString()} to{' '}
            {new Date(props.selected.endTime).toLocaleDateString()}
          </Button>
        </DialogTrigger>
        <DialogContent className='font-sans min-w-xl pt-8'>
          <Calendar
            mode='range'
            defaultMonth={dateRange?.from}
            selected={dateRange}
            onSelect={handleDateRangeChange}
            numberOfMonths={2}
            showOutsideDays={false}
            className='text-sm! self-center place-self-center'
          />
          <div className='flex justify-end'>
            <DialogClose asChild>
              <Button
                variant='default'
                onClick={() => {
                  if (dateRange?.from && dateRange?.to) {
                    const startDate = new Date(dateRange.from);
                    startDate.setUTCHours(0, 0, 0, 0);

                    const endDate = new Date(dateRange.to);
                    endDate.setUTCHours(23, 59, 59, 999);

                    props.onSelect({
                      ...props.selected,
                      startTime: startDate.toISOString(),
                      endTime: endDate.toISOString()
                    });
                  }
                }}
              >
                Apply
              </Button>
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
